import type { AskEntity, AskEntityType } from './ShellContext'

/**
 * What the dock offers to ask, keyed by the surface the merchant is on and the
 * record they have open.
 *
 * §3.2's rule is two-part: suggestions are "surface/entity specific" AND they
 * "never imply an unavailable tool". Where those two pull against each other
 * the second wins, because a suggestion the agent cannot answer is worse than a
 * generic one — it is a promise the product does not keep.
 */

/** The four surfaces in §3.2's context contract. `value` has no screen yet. */
export type AskSurface = 'ledger' | 'knowledge' | 'authority' | 'value'

/**
 * Everything merchant chat can actually do (MAR-173 PR 3): the twelve read
 * capabilities in `services/chat/merchant-chat/src/capabilities/registry.js`
 * `CAPABILITIES`, by name. Merchant chat calls them through the capability
 * plane; there is no other tool.
 *
 * ⚠️ **All twelve are READS.** Chat can change nothing until Phase D, so no
 * suggestion may offer to approve, answer, resolve, send or change anything —
 * the merchant does that in the Inbox. `askSuggestions.test.ts` checks both
 * rules: every suggestion is backed by a name here, and none sounds like a write.
 *
 * ⚠️ Keep this list in step with the registry. It is the allowlist every
 * suggestion is checked against, and a suggestion naming a capability that is
 * not here fails to compile.
 */
export const MERCHANT_CAPABILITIES = [
  'explain_decision',
  'list_attention_items',
  'read_customer_memory',
  'read_product_knowledge',
  'read_authority',
  'read_value',
  'read_sales_report',
  'read_product_report',
  'read_customer_mix',
  'find_orders',
  'find_products',
  'find_customers',
] as const

export type MerchantCapability = (typeof MERCHANT_CAPABILITIES)[number]

export interface AskSuggestion {
  /** What the merchant reads. */
  label: string
  /**
   * The capability that answers it. Recorded rather than commented so a test
   * can check every suggestion against the registry, and so the next person
   * adding one has to say which read they expect to serve it.
   */
  backedBy: MerchantCapability
}

/** Which surface a route is. `null` for the screens §3.2 does not name. */
export function surfaceFor(pathname: string): AskSurface | null {
  if (pathname === '/' || pathname === '/ledger') return 'ledger'
  if (pathname === '/knowledge') return 'knowledge'
  if (pathname === '/authority') return 'authority'
  return null
}

/**
 * Asked anywhere, answerable anywhere. What the merchant gets on a screen §3.2
 * does not name (Docs, Account, Admin) and the floor under every surface.
 */
const GLOBAL: AskSuggestion[] = [
  { label: 'What is waiting on me right now?', backedBy: 'list_attention_items' },
  { label: 'How has the store been doing lately?', backedBy: 'read_sales_report' },
]

const BY_SURFACE: Record<AskSurface, AskSuggestion[]> = {
  ledger: [
    { label: 'What is waiting on me right now?', backedBy: 'list_attention_items' },
    { label: 'How have orders moved this month?', backedBy: 'read_sales_report' },
    { label: 'What has Marva earned me this month?', backedBy: 'read_value' },
  ],
  knowledge: [
    { label: 'What does my customer base look like?', backedBy: 'read_customer_mix' },
    { label: 'What is selling best right now?', backedBy: 'read_product_report' },
    { label: 'What is running low?', backedBy: 'read_product_report' },
  ],
  authority: [
    { label: 'What is Marva allowed to do in my store?', backedBy: 'read_authority' },
    { label: 'Which limits is Marva working within?', backedBy: 'read_authority' },
    { label: 'Is anything waiting on me?', backedBy: 'list_attention_items' },
  ],
  // No screen yet. `read_value` answers it, so the set is real even before a
  // route reaches it.
  value: [
    { label: 'What has Marva earned me this month?', backedBy: 'read_value' },
    { label: 'Which orders does Marva count as hers?', backedBy: 'read_value' },
  ],
}

/**
 * Suggestions for an open record. Every pin type has its own, now that the
 * reads behind them exist: a decision is `explain_decision`, an authority
 * exception is `read_authority`, an attributed order is `read_value`.
 *
 * ⚠️ Questions ABOUT the record, never actions on it. "Approve this" or "reply
 * to her" would be a promise chat cannot keep until Phase D.
 */
const BY_ENTITY: Record<AskEntityType, (label: string) => AskSuggestion[]> = {
  decision: () => [
    { label: 'Why did Marva do this?', backedBy: 'explain_decision' },
    { label: 'What governed this decision?', backedBy: 'explain_decision' },
  ],
  customer: (label) => [
    { label: `What does Marva know about ${label}?`, backedBy: 'read_customer_memory' },
    { label: `How much has ${label} spent with me?`, backedBy: 'find_customers' },
  ],
  product: (label) => [
    { label: `What does Marva know about ${label}?`, backedBy: 'read_product_knowledge' },
    { label: `Is ${label} running low?`, backedBy: 'find_products' },
  ],
  // ⚠️ Both reads below are STORE-WIDE: `read_authority` takes no exception id
  // and `read_value` takes only a window. So the questions are about the store
  // the pin sits in, never about this exception or this order, which neither
  // tool can see.
  authority_exception: () => [
    { label: 'What is Marva allowed to do here?', backedBy: 'read_authority' },
    { label: 'Which limits is Marva working within?', backedBy: 'read_authority' },
  ],
  attribution_order: () => [
    { label: 'How much has Marva earned this month?', backedBy: 'read_value' },
    { label: 'How much has Marva earned this year?', backedBy: 'read_value' },
  ],
}

/**
 * What to offer, given where the merchant is and what they have open.
 *
 * An entity wins over the surface when it has suggestions of its own: the
 * record in front of them is more specific than the page it sits on.
 */
export function suggestionsFor(surface: AskSurface | null, entity: AskEntity | null): AskSuggestion[] {
  const byEntity = entity ? BY_ENTITY[entity.type] : undefined
  if (entity && byEntity) return byEntity(entity.label)
  return surface ? BY_SURFACE[surface] : GLOBAL
}
